// ============================================
// HTML Sanitize 유틸리티
// 허용된 태그만 남기고 나머지는 제거
// ============================================

// 허용되는 HTML 태그 목록
const ALLOWED_TAGS = ['div', 'p', 'span', 'b', 'i', 'strong', 'em', 'a', 'br', 'hr', 'img'];

// 허용되는 속성 (태그별)
const ALLOWED_ATTRIBUTES = {
  a: ['href', 'title'],
  img: ['src', 'alt', 'width', 'height'],
  div: ['class', 'style'],
  p: ['class', 'style'],
  span: ['class', 'style']
};

// 위험한 CSS 값 패턴 (XSS 방지)
// - expression(): IE 구버전 XSS
// - javascript:: 스크립트 실행
// - url(): 외부 리소스 로드 (트래킹 등)
// CSS 속성 이름 자체는 보안 위험 없음, 값만 검증

/**
 * HTML 문자열을 sanitize
 * @param {string} html - 원본 HTML 문자열
 * @returns {string} - sanitize된 HTML 문자열
 */
export function sanitizeHtml(html) {
  if (!html || typeof html !== 'string') return '';

  // DOMParser를 사용하여 안전하게 파싱
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // body의 모든 요소를 순회하며 sanitize
  const sanitized = sanitizeNode(doc.body);

  // DocumentFragment는 innerHTML이 없으므로 임시 div로 변환
  const tempDiv = document.createElement('div');
  tempDiv.appendChild(sanitized);
  return tempDiv.innerHTML;
}

/**
 * 노드를 재귀적으로 sanitize
 * @param {Node} node - DOM 노드
 * @returns {DocumentFragment} - sanitize된 노드
 */
function sanitizeNode(node) {
  const fragment = document.createDocumentFragment();

  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      // 텍스트 노드는 그대로 유지
      fragment.appendChild(document.createTextNode(child.textContent));
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const tagName = child.tagName.toLowerCase();

      if (ALLOWED_TAGS.includes(tagName)) {
        // 허용된 태그인 경우
        const newElement = document.createElement(tagName);

        // 허용된 속성만 복사
        const allowedAttrs = ALLOWED_ATTRIBUTES[tagName] || [];
        for (const attr of allowedAttrs) {
          if (child.hasAttribute(attr)) {
            let value = child.getAttribute(attr);

            // 특별 처리: href, src 검증
            if (attr === 'href') {
              value = sanitizeUrl(value);
            } else if (attr === 'src') {
              value = sanitizeImageUrl(value);
            } else if (attr === 'style') {
              value = sanitizeStyle(value);
            }

            if (value) {
              newElement.setAttribute(attr, value);
            }
          }
        }

        // a 태그에 보안 속성 추가
        if (tagName === 'a') {
          newElement.setAttribute('rel', 'noopener noreferrer');
          newElement.setAttribute('target', '_blank');
        }

        // 자식 노드 재귀 처리
        const childContent = sanitizeNode(child);
        newElement.appendChild(childContent);

        fragment.appendChild(newElement);
      } else {
        // 허용되지 않은 태그는 내용만 유지
        const childContent = sanitizeNode(child);
        fragment.appendChild(childContent);
      }
    }
  }

  return fragment;
}

/**
 * URL sanitize (a 태그용)
 * @param {string} url - 원본 URL
 * @returns {string} - sanitize된 URL 또는 빈 문자열
 */
function sanitizeUrl(url) {
  if (!url) return '';

  // javascript:, data: 등 위험한 프로토콜 차단
  const trimmed = url.trim().toLowerCase();
  if (trimmed.startsWith('javascript:') ||
      trimmed.startsWith('data:') ||
      trimmed.startsWith('vbscript:')) {
    return '';
  }

  // http, https, mailto, tel만 허용
  if (trimmed.startsWith('http://') ||
      trimmed.startsWith('https://') ||
      trimmed.startsWith('mailto:') ||
      trimmed.startsWith('tel:') ||
      trimmed.startsWith('/') ||
      trimmed.startsWith('#')) {
    return url;
  }

  // 상대 경로 허용
  if (!trimmed.includes(':')) {
    return url;
  }

  return '';
}

/**
 * 이미지 URL sanitize
 * @param {string} url - 원본 URL
 * @returns {string} - sanitize된 URL 또는 빈 문자열
 */
function sanitizeImageUrl(url) {
  if (!url) return '';

  const trimmed = url.trim().toLowerCase();

  // http, https만 허용
  if (trimmed.startsWith('http://') ||
      trimmed.startsWith('https://') ||
      trimmed.startsWith('/') ||
      trimmed.startsWith('data:image/')) {
    return url;
  }

  return '';
}

/**
 * style 속성 sanitize
 * 모든 CSS 속성 허용, 위험한 값만 차단
 * @param {string} style - 원본 style 문자열
 * @returns {string} - sanitize된 style 문자열
 */
function sanitizeStyle(style) {
  if (!style) return '';

  const properties = style.split(';').map(p => p.trim()).filter(Boolean);
  const allowed = [];

  for (const prop of properties) {
    const [name, ...valueParts] = prop.split(':');
    if (!name || valueParts.length === 0) continue;

    const propName = name.trim().toLowerCase();
    const propValue = valueParts.join(':').trim();

    // 위험한 값만 차단 (모든 CSS 속성 허용)
    const lowerValue = propValue.toLowerCase();
    if (!lowerValue.includes('expression') &&
        !lowerValue.includes('javascript') &&
        !lowerValue.includes('url(')) {
      allowed.push(`${propName}: ${propValue}`);
    }
  }

  return allowed.join('; ');
}

/**
 * 텍스트를 HTML로 변환 (줄바꿈 → br)
 * @param {string} text - 원본 텍스트
 * @returns {string} - HTML 문자열
 */
export function textToHtml(text) {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
}
